import * as React from "react";
import { Button, Checkbox, HTMLSelect } from "@blueprintjs/core";
import { Popover2 } from "@blueprintjs/popover2";
import { observer } from "mobx-react";
import classNames from "classnames";

import AnnotationAmrSourceFilter from "../annotation-amr-source-filter";
import AnnotationTrack from "../annotation-track";
import AnnotationTrackLegend from "../annotation-track/legend";
import AnnotationDownloadButton from "../annotation-download-button";
import AnnotationSearchInput from "../annotation-search";
import AnnotationTable from "../annotation-table";
import { Intersection } from "../../types";
import { trackSegmentEvent } from "../../utils/analytics";
import { StoresContext } from "../../pages/_app";
import { useContext } from "react";
import { CollectionName } from "../../api";

interface GenomesAnnotationsProps {
  genomeName: string;
  genomeUuid?: string;
  assemblyUuid?: string;
  collectionName?: CollectionName;
}

const GenomesAnnotations = (props: GenomesAnnotationsProps) => {
  const rootStore = useContext(StoresContext);
  const {
    includeHypotheticalProteins,
    canDownload,
    searchTerm,
    isComparison,
    filteredAnnotations,
  } = rootStore.annotationStore;
  const classes = classNames("genomes-annotations", {
    "genomes-annotations--comparison": isComparison,
  });

  const updateSearchTerm = (term: string) => {
    trackSegmentEvent("Filter Annotations", {
      assemblyUuid: props.assemblyUuid,
      searchTerm: term,
      context: "Annotations Viewer",
      genomeName: props.genomeName,
      genomeUuid: props.genomeUuid,
    });
    rootStore.annotationStore.updateSearchTerm(term);
  };

  const renderTracks = () => {
    return rootStore.annotationStore.setUuids.map((uuid, idx) => {
      return <AnnotationTrack uuid={uuid} key={uuid} idx={idx} assemblyUuid={props.assemblyUuid} />;
    });
  };

  const renderIntersectionDropdown = () => {
    if (!isComparison) {
      return null;
    }

    const options = [
      { label: "Intersect By...", value: "" },
      { label: "Gene", value: "gene" },
      { label: "EC number", value: "ec" },
      { label: "RefSeq ID", value: "refseq_id" },
    ];

    return (
      <HTMLSelect
        options={options}
        onChange={(o) =>
          rootStore.annotationStore.toggleIntersection(o.target.value as Intersection)
        }
      />
    );
  };

  const setIsAmr = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const isAmr = e.currentTarget.value === "true";
    rootStore.annotationStore.setIsAmr(isAmr);
  };

  const renderGeneFilterSelect = () => {
    const options = [
      // Note - values must be strings
      { label: "Display All Genes", value: "null" },
      { label: "Display AMR Genes", value: "true" },
    ];

    return (
      <HTMLSelect
        iconProps={{ title: "" /* Otherwise SSR complains */ }}
        options={options}
        onChange={setIsAmr}
      />
    );
  };

  return (
    <div className={classes}>
      <Popover2
        className="legend-popover"
        position="bottom-right"
        content={<AnnotationTrackLegend />}
      >
        <Button text="Annotation Legend" />
      </Popover2>
      {renderTracks()}
      <div className="genomes-annotations__container">
        <Checkbox
          checked={includeHypotheticalProteins}
          onChange={() => rootStore.annotationStore.toggleIncludeHypotheticalProteins()}
          label="Include Hypothetical Proteins"
          className="hp-filter"
        />
        {renderIntersectionDropdown()}
        {renderGeneFilterSelect()}
        <AnnotationAmrSourceFilter />
        <div className="genomes-annotations__filter">
          <AnnotationSearchInput
            leftIcon="search"
            onChange={updateSearchTerm}
            placeholder="Filter annotations"
          />
          <AnnotationDownloadButton
            annotationUuids={filteredAnnotations.map((a) => a.uuid)}
            searchTerm={searchTerm}
            disabled={!canDownload}
            genomeName={props.genomeName}
            genomeUuid={props.genomeUuid}
            assemblyUuid={props.assemblyUuid}
          />
        </div>
      </div>
      <AnnotationTable collectionName={props.collectionName || null} />
    </div>
  );
};

export default observer(GenomesAnnotations);
